import { grantNodeToPrivilegeSet } from "@p0security/shared/assessment/render";
import { isNode } from "@p0security/shared/graph/types";
import { ProviderOrAll } from "@p0security/shared/types/assessment";
import {
  AssessmentNodes,
  GrantNode,
  IdentityNode,
  NodeFor,
} from "@p0security/shared/types/assessment/data/index";
import { GraphTooltip } from "components/GraphTooltip";
import { capitalize } from "lodash";
import { ReactNode } from "react";

import { toConsumerText } from "../cells/ConsumersList";
import { CredentialDisplay } from "../cells/Credential";
import { IdentityCell } from "../cells/Identity";
import { CellInner } from "../cells/Inner";
import { Lateral } from "../cells/Lateral";
import {
  RiskGroupedPrivilegeList,
  toPrivilegeAggregate,
} from "../cells/PrivilegeAggregate";
import { PrivilegeLink } from "../cells/PrivilegeLink";
import { Resource } from "../cells/Resource";
import { HydratedRiskLink } from "../cells/RiskLink";

export const NodeText = (
  current: NodeFor<keyof AssessmentNodes> | undefined,
  provider: ProviderOrAll,
  options?: { detailed?: boolean; hideThis?: boolean; noHover?: boolean }
): { [K in keyof AssessmentNodes]?: (node: NodeFor<K>) => ReactNode } => ({
  consumer: (n) => toConsumerText(n.data),
  credential: (n) => <CredentialDisplay credential={n.data} id={n.key} />,
  grant: (n) => {
    const main =
      !options?.hideThis && current?.type === "grant" && current?.key === n.key
        ? "This grant"
        : grantNodeToPrivilegeSet(n);

    const content = (
      <>
        <CellInner>
          <Resource resource={n} index={0} />
        </CellInner>
        <CellInner style={{ fontStyle: "italic" }}>{main}</CellInner>
      </>
    );
    const detail = (
      <CellInner>
        {content} to{" "}
        <IdentityCell
          identity={{
            ...n.data,
            label: n.data.principal,
            type: n.data.principalType,
          }}
        />
      </CellInner>
    );

    return options?.detailed ? (
      detail
    ) : options?.noHover ? (
      content
    ) : (
      <GraphTooltip width="400px" title={detail}>
        {content}
      </GraphTooltip>
    );
  },
  lateral: (n) => <Lateral node={n} />,
  usage: (n) => {
    const privileges =
      (current as GrantNode | IdentityNode)?.aggregates?.privileges ??
      toPrivilegeAggregate(n);
    // If `count` on data, then this is all-targets view, and the permission
    // aggregate will always be empty
    const hoverElement = !n.data.count && (
      <RiskGroupedPrivilegeList
        privileges={privileges}
        initialSelected={n.data.type}
        provider={provider}
      />
    );
    const labelElement = (
      <>
        {n.data.type === "unknown"
          ? "Potentially used"
          : capitalize(n.data.type)}{" "}
        privileges (
        {n.data.count ?? n.children.filter(isNode("privilege")).length})
      </>
    );
    return hoverElement ? (
      <GraphTooltip width="500px" title={hoverElement}>
        {labelElement}
      </GraphTooltip>
    ) : (
      labelElement
    );
  },
  privilege: (n) => (
    <PrivilegeLink key={n.key} privilege={n.key} provider={provider} />
  ),
  identity: (n) => (
    <IdentityCell identity={n.data} noHover={options?.noHover} />
  ),
  resource: (n) => <Resource resource={n} shortPath />,
  risk: (n) => <HydratedRiskLink key={n.key} id={n.key} />,
});
